import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import kindToTableType from 'notebook/structured-tables/util/kindToTableType';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {
  CustomResolutionQueryType,
  ServerResolutionResult,
  StringResolutionType,
  TransferTableValidateDestinationMatrixPlateQuery,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import APIUtil from 'util/APIUtil';

import {
  GetPlateSchemaTypeQuery,
  GetPlateSchemaTypeQueryVariables,
} from './__generated/GetPlateSchemaTypeQuery';
import extractStorableId from './extractStorableId';

const GET_PLATE_SCHEMA_TYPE_QUERY = gql`
  query GetPlateSchemaTypeQuery($plateIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    storables(ids: $plateIds) {
      ... on PlateNode {
        id
        storableSchema {
          ... on PlateSchemaNode {
            id
            plateSchemaType
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const stubbableFunctions = {
  async doQuery(plateId: string): Promise<GetPlateSchemaTypeQuery> {
    return await doQueryWithoutCache<GetPlateSchemaTypeQuery, GetPlateSchemaTypeQueryVariables>(
      {query: GET_PLATE_SCHEMA_TYPE_QUERY},
      {
        variables: {plateIds: [plateId]},
      }
    );
  },
};

export async function validateDestinationMatrixPlate(
  query: TransferTableValidateDestinationMatrixPlateQuery
): Promise<ServerResolutionResult> {
  const data = await stubbableFunctions.doQuery(query.plateId);
  const plate = data.storables[0];
  if (isInaccessible(plate)) {
    return {
      type: StringResolutionType.CUSTOM,
      error: undefined,
      resolvedValue: {typedValue: null},
    };
  }

  assert(plate.__typename === 'PlateNode', 'Expected a plate');
  assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');
  if (plate.storableSchema.plateSchemaType === PlateSchemaType.FIXED_PLATE) {
    return {
      type: undefined,
      error: 'Must be a matrix plate, not a plate with fixed wells',
      resolvedValue: undefined,
    };
  }

  // We don't need to check the schema. Unlike Destination Plate, Destination Matrix Plate can be any schema
  // (assuming it's in the right registry).
  return {
    type: StringResolutionType.CUSTOM,
    error: undefined,
    resolvedValue: {typedValue: null},
  };
}

export function destinationMatrixPlateCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  cellDependencies: [],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn,
  resolveTypedValueToType: ResolveTypedValueToTypeFn
): CustomEvalResult {
  // TODO(BNCH-15744): customEval currently runs _before_ string resolution and this particular resolution
  // would like to operate on a post-resolved value, so we manually fire it off first.
  const {resolvedValue: stringResolvedValue} = resolveTypedValueToType(columnDefinition, typedValue, {
    tableType: kindToTableType(StructuredTableKind.TRANSFER, false),
  });
  if (stringResolvedValue.type !== BaseTypedValueType.STORABLES) {
    return {val: typedValue, error: null, locked: false};
  }

  const plateId = extractStorableId(stringResolvedValue);
  if (plateId == null || !APIUtil.isPlate(plateId)) {
    // TODO(BNCH-15744): the evaluator currently runs customEval before resolveTypedValue,
    // so by returning the original typed value here, we're falling back to the default validation logic
    // in resolveTypedValue
    return {val: typedValue, error: null, locked: false};
  }

  const {resolvedValue, error} = resolveFromResolutionQuery(
    stringResolvedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_MATRIX_PLATE,
      plateId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}
